import React, { useState, useEffect } from 'react';
import {
  branchService,
  locationService,
  businessCategoryService,
  organizationService,
} from '../services';
import { Branch, BusinessCategory, Organization } from '../types';
import { City } from '../services/locations';
import { Pagination } from '../components/Pagination';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Plus, Loader2, MapPin, Building2, Edit, Trash2, Eye } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';

interface BranchFormData {
  organizationId: string;
  name: string;
  description?: string;
  address: string;
  cityId: string;
  businessCategoryIds: number[];
  commissionRate?: number | '';
  baseCommission?: number | '';
  minPeople?: number | '';
  maxPeople?: number | '';
  latitude?: number | '';
  longitude?: number | '';
  phone?: string;
  email?: string;
  website?: string;
}

export const BranchesPage: React.FC = () => {
  const navigate = useNavigate();
  const [branches, setBranches] = useState<Branch[]>([]);
  const [cities, setCities] = useState<City[]>([]);
  const [businessCategories, setBusinessCategories] = useState<BusinessCategory[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState<number[]>([]);
  const [editingBranch, setEditingBranch] = useState<Branch | null>(null);
  const [branchImages, setBranchImages] = useState<string[]>([]);
  const [imagesModified, setImagesModified] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm<BranchFormData>();

  useEffect(() => {
    loadBranches(currentPage, pageSize);
    loadCities();
    loadBusinessCategories();
    loadOrganizations();
  }, [currentPage, pageSize]);

  const loadBranches = async (page: number = 0, size: number = 10) => {
    try {
      setLoading(true);
      setError(null);
      const response = await branchService.getAllBranches(page, size);
      setBranches(response.content);
      setTotalElements(response.totalElements);
      setTotalPages(response.totalPages);
    } catch (err: any) {
      setError(err.message || 'Failed to load branches');
    } finally {
      setLoading(false);
    }
  };

  const loadCities = async () => {
    try {
      const citiesData = await locationService.getAllCities();
      setCities(citiesData);
    } catch (err: any) {
      console.error('Failed to load cities:', err);
    }
  };

  const loadBusinessCategories = async () => {
    try {
      const categoriesData = await businessCategoryService.getAllCategoriesAdmin(false);
      setBusinessCategories(categoriesData);
    } catch (err: any) {
      console.error('Failed to load business categories:', err);
    }
  };

  const loadOrganizations = async () => {
    try {
      const response = await organizationService.getAllOrganizations(0, 100);
      setOrganizations(response.content);
    } catch (err: any) {
      console.error('Failed to load organizations:', err);
    }
  };

  const handleOpenDialog = async (branch?: Branch) => {
    if (branch) {
      setEditingBranch(branch);
      setImagesModified(false);
      // Load full branch details
      try {
        const fullBranch = await branchService.getBranchById(String(branch.id));
        setSelectedCategories(fullBranch.businessCategories.map((cat) => cat.id));
        setBranchImages(fullBranch.images || []);

        // Find the city ID
        const city = cities.find((c) => c.name === fullBranch.city?.name);

        reset({
          organizationId: String(fullBranch.organizationId),
          name: fullBranch.name,
          description: fullBranch.description || '',
          address: fullBranch.address,
          cityId: city ? String(city.id) : '',
          businessCategoryIds: fullBranch.businessCategories.map((cat) => cat.id),
          commissionRate: fullBranch.commissionRate ?? '',
          baseCommission: fullBranch.baseCommission ?? '',
          minPeople: fullBranch.minPeople ?? '',
          maxPeople: fullBranch.maxPeople ?? '',
          latitude: fullBranch.latitude ?? '',
          longitude: fullBranch.longitude ?? '',
          phone: fullBranch.phone || '',
          email: fullBranch.email || '',
          website: fullBranch.website || '',
        });
      } catch (err: any) {
        console.error('Failed to load branch details:', err);
        setError('Failed to load branch details');
      }
    } else {
      setEditingBranch(null);
      setSelectedCategories([]);
      setBranchImages([]);
      setImagesModified(false);
      reset({
        organizationId: '',
        name: '',
        description: '',
        address: '',
        cityId: '',
        latitude: '',
        longitude: '',
        phone: '',
        email: '',
        website: '',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedCategories([]);
    setBranchImages([]);
    setImagesModified(false);
    setEditingBranch(null);
    reset({});
  };

  const toggleCategory = (categoryId: number) => {
    setSelectedCategories((prev) =>
      prev.includes(categoryId) ? prev.filter((id) => id !== categoryId) : [...prev, categoryId]
    );
  };

  const onSubmit = async (data: BranchFormData) => {
    try {
      if (selectedCategories.length === 0) {
        setError('Please select at least one business category');
        return;
      }

      // Helper function to convert string to number or null
      const toNumberOrNull = (value: number | string | '' | undefined | null) => {
        if (value === '' || value === undefined || value === null) return null;
        const num = Number(value);
        return isNaN(num) ? null : num;
      };

      if (editingBranch) {
        // Update branch - only send changed fields
        const updatePayload: any = {
          name: data.name,
          description: data.description || undefined,
          address: data.address,
          cityId: data.cityId ? parseInt(data.cityId) : undefined,
          businessCategoryIds: selectedCategories,
          commissionRate: toNumberOrNull(data.commissionRate),
          baseCommission: toNumberOrNull(data.baseCommission),
          minPeople: toNumberOrNull(data.minPeople),
          maxPeople: toNumberOrNull(data.maxPeople),
          latitude: toNumberOrNull(data.latitude),
          longitude: toNumberOrNull(data.longitude),
          phone: data.phone || null,
          email: data.email || null,
          website: data.website || null,
        };

        // Only include images if they were modified
        if (imagesModified) {
          updatePayload.images = branchImages;
        }

        await branchService.updateBranch(String(editingBranch.id), updatePayload);
      } else {
        // Create branch - cityId is required
        await branchService.createBranch({
          name: data.name,
          description: data.description,
          address: data.address,
          cityId: parseInt(data.cityId),
          businessCategoryIds: selectedCategories,
          commissionRate: toNumberOrNull(data.commissionRate),
          baseCommission: toNumberOrNull(data.baseCommission),
          minPeople: toNumberOrNull(data.minPeople),
          maxPeople: toNumberOrNull(data.maxPeople),
          latitude: toNumberOrNull(data.latitude),
          longitude: toNumberOrNull(data.longitude),
          phone: data.phone || null,
          email: data.email || null,
          website: data.website || null,
        });
      }

      await loadBranches(currentPage, pageSize);
      handleCloseDialog();
    } catch (err: any) {
      setError(err.message || `Failed to ${editingBranch ? 'update' : 'create'} branch`);
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this branch?')) {
      try {
        await branchService.deleteBranch(id);
        await loadBranches(currentPage, pageSize);
      } catch (err: any) {
        setError(err.message || 'Failed to delete branch');
      }
    }
  };

  if (loading && branches.length === 0) {
    return (
      <div className="flex h-96 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Branches</h1>
          <p className="text-muted-foreground">Manage organization branches and locations</p>
        </div>
        <Button onClick={() => handleOpenDialog()}>
          <Plus className="h-4 w-4 mr-2" />
          Add Branch
        </Button>
      </div>

      {error && (
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-destructive">
          <p className="font-medium">Error</p>
          <p className="text-sm">{error}</p>
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>All Branches</CardTitle>
          <CardDescription>List of all branches across organizations</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Branch Name</TableHead>
                <TableHead>Organization</TableHead>
                <TableHead>Location</TableHead>
                <TableHead>Categories</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {branches.map((branch) => (
                <TableRow key={branch.id}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Building2 className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <p className="font-medium">{branch.name}</p>
                        {branch.description && (
                          <p className="text-sm text-muted-foreground">{branch.description}</p>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {branch.organizationName || '-'}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1 text-sm">
                      <MapPin className="h-3 w-3" />
                      {branch.address || 'No address'}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {branch.businessCategories?.slice(0, 2).map((cat) => (
                        <Badge key={cat.id} variant="outline" className="text-xs">
                          {cat.name}
                        </Badge>
                      ))}
                      {branch.businessCategories && branch.businessCategories.length > 2 && (
                        <span className="text-xs text-muted-foreground">
                          +{branch.businessCategories.length - 2}
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant={branch.isMainBranch ? 'default' : 'secondary'}>
                      {branch.isMainBranch ? 'Main' : 'Branch'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(`/branches/${branch.id}`)}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleOpenDialog(branch)}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(String(branch.id))}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {branches.length === 0 && (
            <p className="text-center text-sm text-muted-foreground py-8">No branches found</p>
          )}

          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalElements={totalElements}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
            onPageSizeChange={setPageSize}
            itemName="branches"
          />
        </CardContent>
      </Card>

      {/* Create Branch Dialog */}
      <Dialog open={openDialog} onOpenChange={setOpenDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingBranch ? 'Edit Branch' : 'Create New Branch'}</DialogTitle>
            <DialogDescription>
              {editingBranch ? 'Update branch information' : 'Add a new branch to an organization'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label htmlFor="name">Branch Name *</Label>
                <Input id="name" {...register('name', { required: true })} />
                {errors.name && <p className="text-sm text-destructive">Branch name is required</p>}
              </div>

              <div className="col-span-2">
                <Label htmlFor="description">Description</Label>
                <Textarea id="description" {...register('description')} />
              </div>

              <div className="col-span-2">
                <Label htmlFor="address">Address *</Label>
                <Input id="address" {...register('address', { required: true })} />
                {errors.address && <p className="text-sm text-destructive">Address is required</p>}
              </div>

              <div className="col-span-2">
                <Label htmlFor="cityId">City *</Label>
                <Controller
                  name="cityId"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <Select onValueChange={field.onChange} value={field.value}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a city" />
                      </SelectTrigger>
                      <SelectContent>
                        {cities.map((city) => (
                          <SelectItem key={city.id} value={String(city.id)}>
                            {city.name} {city.countryName ? `(${city.countryName})` : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
                {errors.cityId && <p className="text-sm text-destructive">City is required</p>}
              </div>

              <div className="col-span-2">
                <Label>Business Categories *</Label>
                <div className="border rounded-md p-3 max-h-40 overflow-y-auto space-y-2">
                  {businessCategories.map((category) => (
                    <label
                      key={category.id}
                      className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded"
                    >
                      <input
                        type="checkbox"
                        checked={selectedCategories.includes(category.id)}
                        onChange={() => toggleCategory(category.id)}
                        className="rounded"
                      />
                      <span className="text-sm">{category.name}</span>
                    </label>
                  ))}
                </div>
                {selectedCategories.length === 0 && (
                  <p className="text-sm text-muted-foreground mt-1">Select at least one category</p>
                )}
              </div>

              <div className="col-span-2">
                <Label htmlFor="commissionRate">
                  Commission Rate (%) - Optional, clear to remove
                </Label>
                <Input
                  id="commissionRate"
                  type="text"
                  placeholder="e.g., 10.5 (leave empty for none)"
                  {...register('commissionRate')}
                />
              </div>

              <div className="col-span-2">
                <Label htmlFor="baseCommission">Base Commission - Optional, clear to remove</Label>
                <Input
                  id="baseCommission"
                  type="text"
                  placeholder="e.g., 100 (leave empty for none)"
                  {...register('baseCommission')}
                />
              </div>

              <div>
                <Label htmlFor="minPeople">Min People - Optional, clear to remove</Label>
                <Input
                  id="minPeople"
                  type="text"
                  placeholder="e.g., 5 (leave empty for none)"
                  {...register('minPeople')}
                />
              </div>

              <div>
                <Label htmlFor="maxPeople">Max People - Optional, clear to remove</Label>
                <Input
                  id="maxPeople"
                  type="text"
                  placeholder="e.g., 50 (leave empty for none)"
                  {...register('maxPeople')}
                />
              </div>

              <div>
                <Label htmlFor="latitude">Latitude - Optional, clear to remove</Label>
                <Input
                  id="latitude"
                  type="number"
                  step="any"
                  placeholder="e.g., 41.0082 (leave empty for none)"
                  {...register('latitude')}
                />
              </div>

              <div>
                <Label htmlFor="longitude">Longitude - Optional, clear to remove</Label>
                <Input
                  id="longitude"
                  type="number"
                  step="any"
                  placeholder="e.g., 28.9784 (leave empty for none)"
                  {...register('longitude')}
                />
              </div>

              <div className="col-span-2">
                <Label htmlFor="phone">Phone</Label>
                <Input id="phone" type="tel" placeholder="Optional" {...register('phone')} />
              </div>

              <div className="col-span-2">
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" placeholder="Optional" {...register('email')} />
              </div>

              <div className="col-span-2">
                <Label htmlFor="website">Website</Label>
                <Input
                  id="website"
                  type="url"
                  placeholder="https://example.com"
                  {...register('website')}
                />
              </div>

              {editingBranch && (
                <div className="col-span-2">
                  <Label>Branch Images ({branchImages.length})</Label>
                  {branchImages.length > 0 ? (
                    <div className="mt-2 space-y-2">
                      <p className="text-sm text-muted-foreground">Current images (S3 keys):</p>
                      <div className="border rounded-md p-3 max-h-40 overflow-y-auto space-y-1">
                        {branchImages.map((imageKey, index) => (
                          <div
                            key={index}
                            className="flex items-center justify-between p-2 bg-gray-50 rounded"
                          >
                            <span className="text-xs font-mono truncate flex-1">{imageKey}</span>
                            <button
                              type="button"
                              onClick={() => {
                                setBranchImages(branchImages.filter((_, i) => i !== index));
                                setImagesModified(true);
                              }}
                              className="ml-2 text-red-600 hover:text-red-800 text-sm"
                            >
                              Remove
                            </button>
                          </div>
                        ))}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Note: To add new images, use the file upload flow via the backend API.
                        Removing images here will delete them when you save.
                      </p>
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground mt-2">
                      No images uploaded. Use the file upload API to add images.
                    </p>
                  )}
                </div>
              )}
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseDialog}>
                Cancel
              </Button>
              <Button type="submit">{editingBranch ? 'Update' : 'Create'} Branch</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BranchesPage;
